import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ChevronLeft, Check, Home } from 'lucide-react';

const buildCalendlyEmbedUrl = (rawUrl, name, email) => {
  if (!rawUrl) return '';
  try {
    const u = new URL(rawUrl);
    // Calendly inline-iframe params (these are documented and safe).
    u.searchParams.set('embed_domain', window.location.hostname);
    u.searchParams.set('embed_type', 'Inline');
    if (name) u.searchParams.set('name', name);
    if (email) u.searchParams.set('email', email);
    return u.toString();
  } catch (_e) {
    return rawUrl;
  }
};

const RecommendationPricing = ({ recommendation, tr }) => {
  const { pricing } = recommendation;

  if (pricing.type === 'setup + monthly') {
    return (
      <div>
        <p className="text-3xl font-bold text-gray-900">
          ${pricing.setupPrice}{' '}
          <span className="text-lg font-normal text-gray-500 align-text-bottom">USD</span>{' '}
          <span className="text-sm font-normal text-gray-500 align-text-bottom">{tr('pricing.vat')}</span>
        </p>
        <p className="text-sm text-gray-500 mb-2">{tr('wizard.recommendation.initialSetupFee')}</p>
        <p className="text-sm text-gray-500 italic">{tr('wizard.recommendation.installmentsAvailable')}</p>
        <p className="text-xl font-semibold text-blue-900 mt-3">
          + ${pricing.monthlyPrice}{' '}
          <span className="text-sm font-normal text-gray-500">USD</span>
          {tr('wizard.recommendation.monthSuffix')}{' '}
          <span className="text-xs font-normal text-gray-500">{tr('pricing.vat')}</span>
        </p>
        <p className="text-sm text-gray-500">{tr('wizard.recommendation.commitment')}</p>
      </div>
    );
  }

  if (pricing.type === 'custom') {
    return (
      <div>
        <p className="text-gray-700 leading-relaxed">{tr('wizard.recommendation.customPricingNote')}</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-3xl font-bold text-gray-900">
        ${pricing.price}{' '}
        <span className="text-lg font-normal text-gray-500 align-text-bottom">USD</span>{' '}
        <span className="text-sm font-normal text-gray-500 align-text-bottom">{tr('pricing.vat')}</span>
      </p>
      <p className="text-sm text-gray-500">{tr('wizard.recommendation.oneTimeFee')}</p>
      <p className="text-sm text-gray-500 italic mt-1">{tr('wizard.recommendation.installmentsAvailable')}</p>
    </div>
  );
};

const formatScheduledDateTime = (iso, language) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(language === 'es' ? 'es-MX' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_e) {
    return null;
  }
};

// Inline pricing summary used INSIDE the compact one-line confirmation.
// Returns "$X USD" for one-time, "$X USD initial setup fee + $Y USD/month" for
// setup+monthly. Returns null when no concrete price (custom routes) — caller
// must then fall back to packageSummaryCustom.
const inlinePriceText = (recommendation, tr) => {
  const p = recommendation?.pricing;
  if (!p) return null;
  if (p.type === 'one-time' && p.price != null) {
    return `$${p.price} USD`;
  }
  if (p.type === 'setup + monthly' && p.setupPrice != null && p.monthlyPrice != null) {
    return `$${p.setupPrice} USD ${tr('wizard.recommendation.initialSetupFee')} + $${p.monthlyPrice} USD${tr('wizard.recommendation.monthSuffix')}`;
  }
  return null; // custom
};

export const RecommendationScreen = ({
  recommendation,
  userSelectedPackage,
  hasConflict,
  calendlyScheduled,
  calendlyEventDateTime,
  calendlyUrl,
  prefillName,
  prefillEmail,
  language,
  tr,
  onBackToWizard,
  onReturnHome,
  onRestart,
}) => {
  const routeKey = recommendation.route; // startCorp | launchCorp | runCorp | customProject
  const recTitle = tr(`wizard.recommendation.${routeKey}Title`);
  const priceText = inlinePriceText(recommendation, tr);

  // Compact one-sentence package summary shown right under the title.
  const compactSummary = priceText
    ? (
        <>
          {tr('wizard.recommendation.packageSummaryPrefix')}{' '}
          <strong className="text-gray-900">
            {recTitle} ({priceText})
          </strong>
          . {tr('wizard.recommendation.packageSummarySuffix')}
        </>
      )
    : tr('wizard.recommendation.packageSummaryCustom');

  // Final "all set" screen — shown after Calendly event_scheduled
  if (calendlyScheduled) {
    const dt = formatScheduledDateTime(calendlyEventDateTime, language);
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-blue-900 shadow-xl" data-testid="wizard-all-set">
            <CardContent className="p-8 md:p-12 text-center">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Garamond, serif' }}>
                {tr('wizard.recommendation.allSetTitle')}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6 max-w-xl mx-auto">
                {tr('wizard.recommendation.allSetBody')}
              </p>

              <div className="bg-gray-50 rounded-lg p-5 mb-8 text-left max-w-md mx-auto">
                <p className="text-sm font-semibold text-gray-700 mb-1">{tr('wizard.recommendation.title')}</p>
                <p className="text-blue-900 font-semibold mb-3">{recTitle}</p>
                {dt && (
                  <>
                    <p className="text-sm font-semibold text-gray-700 mb-1">
                      {tr('wizard.recommendation.callScheduledLabel')}
                    </p>
                    <p className="text-gray-900">{dt}</p>
                  </>
                )}
              </div>

              <Button
                className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-6"
                onClick={onReturnHome}
                data-testid="wizard-return-home-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                {tr('wizard.recommendation.returnHome')}
              </Button>

              {onRestart && (
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={onRestart}
                    className="text-sm text-gray-500 hover:text-blue-900 underline underline-offset-4 transition-colors"
                    data-testid="wizard-restart-btn-allset"
                  >
                    {tr('wizard.recommendation.restart')}
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="border-2 border-blue-900 shadow-xl">
          <CardContent className="p-8 md:p-12">
            {/* Compact confirmation header */}
            <div className="text-center mb-8" data-testid="wizard-request-submitted">
              <div className="w-16 h-16 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Garamond, serif' }}>
                {tr('wizard.recommendation.requestSubmittedTitle')}
              </h2>
              <p className="text-base text-gray-700 leading-relaxed max-w-xl mx-auto">
                {compactSummary}
              </p>
            </div>

            {/* Embedded Calendly section (iframe-only embed for reliability) */}
            <div className="mb-8" data-testid="wizard-calendly-section">
              <h3
                className="text-2xl font-semibold text-gray-900 mb-2"
                style={{ fontFamily: 'Garamond, "EB Garamond", serif' }}
              >
                {tr('wizard.recommendation.scheduleTitle')}
              </h3>
              <p className="text-gray-600 mb-5 leading-relaxed">
                {tr('wizard.recommendation.scheduleDesc')}
              </p>
              <div
                className="rounded-lg overflow-hidden border border-gray-200"
                data-testid="wizard-calendly-embed"
              >
                <iframe
                  title="Calendly inline scheduler"
                  src={buildCalendlyEmbedUrl(calendlyUrl, prefillName, prefillEmail)}
                  width="100%"
                  height="760"
                  frameBorder="0"
                  allow="camera; microphone; fullscreen"
                />
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full border-gray-300 hover:border-blue-900 hover:text-blue-900"
              onClick={onBackToWizard}
              data-testid="wizard-back-to-wizard-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {tr('wizard.recommendation.backToWizard')}
            </Button>

            {onRestart && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={onRestart}
                  className="text-sm text-gray-500 hover:text-blue-900 underline underline-offset-4 transition-colors"
                  data-testid="wizard-restart-btn"
                >
                  {tr('wizard.recommendation.restart')}
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
